import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { authService } from '@/services/auth.service';
import { getCurrentDomain } from '@/utils/domain';
import {
  extractErrorStatus,
  extractJsonApiErrorCode,
  extractJsonApiErrorMessage,
  extractJsonApiFieldErrors,
} from '@/lib/jsonapi/errors';

type FormValues = {
  email: string;
};

export default function ProviderForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('navigation.providerAccount');

  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled, isLoading: isAvailabilityLoading } =
    usePasswordLoginAvailability('provider', {
      tenantSlug: resolvedTenantSlug,
    });

  const redirectUrl = useMemo(
    () => (typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined),
    [],
  );

  const schema = useMemo(
    () =>
      z.object({
        email: z.string().email(t('auth.invalidEmail')),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      authService.requestProviderPasswordReset({
        email: values.email,
        redirectUrl,
      }),
    onSuccess: () => {
      setErrorMessage(null);
      setStatusMessage(t('auth.forgotPassword.success'));
      form.clearErrors();
    },
    onError: (error: unknown) => {
      setStatusMessage(null);
      const status = extractErrorStatus(error);
      const code = extractJsonApiErrorCode(error);
      const fieldErrors = extractJsonApiFieldErrors(error);

      Object.entries(fieldErrors).forEach(([field, message]) => {
        if (field === 'email') {
          form.setError('email', { message });
        }
      });

      let message = extractJsonApiErrorMessage(error, t('auth.forgotPassword.error'));
      if (status === 423 || code === 'PASSWORD_LOGIN_DISABLED') {
        message = t('auth.passwordLoginDisabled');
      }
      setErrorMessage(message);
    },
  });

  const handleSubmit = (values: FormValues) => {
    setErrorMessage(null);
    setStatusMessage(null);
    mutation.mutate(values);
  };

  const isFormDisabled =
    mutation.isPending || isAvailabilityLoading || !passwordLoginEnabled;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-1">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-2xl font-semibold text-foreground">{brandName}</span>
          )}
        </div>

        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t('auth.forgotPassword.title')}</CardTitle>
            <CardDescription>{t('auth.forgotPassword.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!passwordLoginEnabled && !isAvailabilityLoading ? (
              <Alert variant="destructive">
                <AlertDescription>{t('auth.passwordLoginDisabled')}</AlertDescription>
              </Alert>
            ) : null}

            {errorMessage ? (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}

            {statusMessage ? (
              <Alert>
                <AlertDescription>{statusMessage}</AlertDescription>
              </Alert>
            ) : null}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder={t('auth.emailPlaceholder')}
                          disabled={isFormDisabled}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isFormDisabled}>
                  {mutation.isPending
                    ? t('auth.forgotPassword.submitting')
                    : t('auth.forgotPassword.submit')}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/provider/login')}
            >
              {t('auth.forgotPassword.backToLogin')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
